import { useMemo } from 'react';

import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryDefinitionMetrics } from '@/hooks/discovery/definitionMetrics';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  description: string | null;
  type: string | null;
};

type MetricDefinitionListNode = {
  uniqueId: string;
  description: string | null;
  type: string | null;
};

const getTableData = (
  metric: MetricDefinitionListNode | undefined | null,
): TableData | undefined => {
  if (!metric) return undefined;
  const uniqueId = parseUniqueId(metric.uniqueId);
  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    description: metric.description,
    type: metric.type,
  };
};

export const MetricFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const paginatedMetricQuery = useDiscoveryDefinitionMetrics({
    environmentId,
    userId,
    first: pageSize,
  });
  const metrics = paginatedMetricQuery.data;
  const pageInfo = paginatedMetricQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return metrics?.map(getTableData);
  }, [metrics]);

  if (paginatedMetricQuery.error) return <>{paginatedMetricQuery.error.stack}</>;
  const isLoadingMore = paginatedMetricQuery.fetching;
  const isInitialLoad =
    paginatedMetricQuery.fetching && paginatedMetricQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Metrics</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="metrics"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            size: 60,
            minSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Type',
            accessorKey: 'type',
            size: 20,
            minSize: 100,
            cell: TruncatedCell,
          },
          {
            header: 'Description',
            accessorKey: 'description',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedMetricQuery.fetchMore}
      />
    </>
  );
};
